import React, { useState, useEffect } from "react";
import {
  XMarkIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowTopRightOnSquareIcon,
  UserIcon,
  ChartBarIcon,
  PhotoIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import LoadingSpinner from "./LoadingSpinner";

const ConnectAccountModal = ({
  isOpen,
  onClose,
  platform,
  platformKey,
  onConnect,
}) => {
  const [step, setStep] = useState("permissions"); // permissions, connecting, success, error
  const [error, setError] = useState(null);
  const [authUrl, setAuthUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [permissionsExpanded, setPermissionsExpanded] = useState(false);

  const permissionCategories = {
    basic: {
      icon: UserIcon,
      title: "Basic Profile Information",
      description: "Access to your public profile details",
      items: [
        "Profile name",
        "Profile picture",
        "Username/handle",
        "Follower count",
      ],
    },
    content: {
      icon: PhotoIcon,
      title: "Content & Posts",
      description: "Read your posts and content",
      items: [
        "Recent posts",
        "Post engagement metrics",
        "Content performance",
        "Publishing history",
      ],
    },
    analytics: {
      icon: ChartBarIcon,
      title: "Analytics & Insights",
      description: "Access to your account analytics",
      items: [
        "Engagement rates",
        "Reach and impressions",
        "Follower demographics",
        "Performance trends",
      ],
    },
    publishing: {
      icon: DocumentTextIcon,
      title: "Publishing (Optional)",
      description: "Ability to publish content on your behalf",
      items: [
        "Schedule posts",
        "Publish content",
        "Manage drafts",
        "Cross-platform publishing",
      ],
      optional: true,
    },
  };

  const securityFeatures = [
    "OAuth 2.0 standard authentication",
    "Encrypted data transmission (TLS 1.3)",
    "Token-based access (no password storage)",
    "Revoke access anytime",
    "Read-only by default",
    "SOC2 Type II compliant",
  ];

  useEffect(() => {
    if (isOpen) {
      setStep("permissions");
      setError(null);
      setAuthUrl(null);
    }
  }, [isOpen]);

  const handleConnect = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Get OAuth authorization URL
      const response = await fetch("/api/oauth/authorize-url", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
        body: JSON.stringify({
          platform: platformKey,
          scopes: platform.oauth_scope.split(","),
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to get authorization URL");
      }

      const data = await response.json();
      setAuthUrl(data.auth_url);
      setStep("connecting");

      // Open OAuth popup
      const popup = window.open(
        data.auth_url,
        "oauth_popup",
        "width=600,height=700,scrollbars=yes,resizable=yes",
      );

      // Listen for OAuth completion
      const pollTimer = setInterval(() => {
        try {
          if (popup.closed) {
            clearInterval(pollTimer);
            handleOAuthComplete();
          }
        } catch (error) {
          // Cross-origin error when popup is still on OAuth provider
        }
      }, 1000);

      // Listen for messages from popup
      const handleMessage = (event) => {
        if (event.origin !== window.location.origin) return;

        if (event.data.type === "oauth_success") {
          clearInterval(pollTimer);
          popup.close();
          window.removeEventListener("message", handleMessage);
          handleOAuthSuccess(event.data.account);
        } else if (event.data.type === "oauth_error") {
          clearInterval(pollTimer);
          popup.close();
          window.removeEventListener("message", handleMessage);
          handleOAuthError(event.data.error);
        }
      };

      window.addEventListener("message", handleMessage);
    } catch (err) {
      setError(err.message);
      setStep("error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOAuthComplete = async () => {
    try {
      // Check for completed OAuth
      const response = await fetch("/api/oauth/check-status", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.status === "completed" && data.account) {
          handleOAuthSuccess(data.account);
        } else if (data.status === "error") {
          handleOAuthError(data.error);
        }
      }
    } catch (err) {
      handleOAuthError("Connection failed");
    }
  };

  const handleOAuthSuccess = (account) => {
    setStep("success");
    setTimeout(() => {
      onConnect(account);
    }, 2000);
  };

  const handleOAuthError = (errorMessage) => {
    setError(errorMessage);
    setStep("error");
  };

  const renderPermissionsStep = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div
          className={`inline-flex items-center justify-center w-16 h-16 rounded-full ${platform.color} ${platform.textColor} mb-4`}
        >
          <span className="text-2xl">{platform.icon}</span>
        </div>
        <h2 className="text-xl font-semibold text-gray-900">
          Connect {platform.name} Account
        </h2>
        <p className="text-gray-600 mt-2">
          Review the permissions and connect your account securely
        </p>
        {/* Instagram Business Account Notice */}
        {platformKey === "instagram" && (
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mt-4">
            <div className="flex items-start">
              <ExclamationTriangleIcon className="h-5 w-5 text-purple-400 mr-3 mt-0.5 flex-shrink-0" />
              <div className="text-left">
                <h3 className="text-sm font-medium text-purple-800">
                  Instagram Business Account Required
                </h3>
                <p className="text-sm text-purple-700 mt-1">
                  To access Instagram analytics and insights, you need an
                  Instagram Business or Creator account connected to a Facebook
                  Page.
                </p>
                <ul className="text-xs text-purple-600 mt-2 space-y-1">
                  <li className="flex items-center">
                    <div className="w-1 h-1 bg-purple-500 rounded-full mr-2" />
                    Convert to Business/Creator account in Instagram settings
                  </li>
                  <li className="flex items-center">
                    <div className="w-1 h-1 bg-purple-500 rounded-full mr-2" />
                    Connect your Instagram to a Facebook Page
                  </li>
                  <li className="flex items-center">
                    <div className="w-1 h-1 bg-purple-500 rounded-full mr-2" />
                    Enable professional features for full analytics
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Security Assurance */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-start">
          <ShieldCheckIcon className="h-5 w-5 text-green-400 mr-3 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-green-800">
              Secure Connection
            </h3>
            <p className="text-sm text-green-700 mt-1">
              We use industry-standard OAuth 2.0 for secure authentication. Your
              credentials are never stored, and you can revoke access at any
              time.
            </p>
          </div>
        </div>
      </div>

      {/* Permissions */}
      <div className="space-y-4">
        <h3 className="font-medium text-gray-900">Required Permissions</h3>

        {Object.entries(permissionCategories).map(([key, category]) => {
          if (category.optional && !permissionsExpanded) return null;

          const IconComponent = category.icon;

          return (
            <div key={key} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start">
                <IconComponent className="h-5 w-5 text-primary-600 mr-3 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-gray-900">
                    {category.title}
                    {category.optional && (
                      <span className="ml-2 bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                        Optional
                      </span>
                    )}
                  </h4>
                  <p className="text-sm text-gray-600 mt-1">
                    {category.description}
                  </p>
                  <ul className="mt-2 space-y-1">
                    {category.items.map((item) => (
                      <li
                        key={item}
                        className="flex items-center text-xs text-gray-500"
                      >
                        <div className="w-1 h-1 bg-gray-400 rounded-full mr-2" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          );
        })}

        {!permissionsExpanded && (
          <button
            onClick={() => setPermissionsExpanded(true)}
            className="text-primary-600 hover:text-primary-700 text-sm font-medium"
          >
            Show all permissions →
          </button>
        )}
      </div>

      {/* Platform Capabilities */}
      {platformKey === "instagram" && (
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 mb-3">
            Instagram Analytics Capabilities
          </h4>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <div className="text-xs font-medium text-purple-800">
                Content Metrics
              </div>
              <div className="space-y-1">
                <div className="flex items-center text-xs text-gray-600">
                  <div className="w-1.5 h-1.5 bg-purple-500 rounded-full mr-2" />
                  Posts & Stories
                </div>
                <div className="flex items-center text-xs text-gray-600">
                  <div className="w-1.5 h-1.5 bg-purple-500 rounded-full mr-2" />
                  Reels Performance
                </div>
                <div className="flex items-center text-xs text-gray-600">
                  <div className="w-1.5 h-1.5 bg-purple-500 rounded-full mr-2" />
                  Engagement Rates
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <div className="text-xs font-medium text-purple-800">
                Audience Insights
              </div>
              <div className="space-y-1">
                <div className="flex items-center text-xs text-gray-600">
                  <div className="w-1.5 h-1.5 bg-pink-500 rounded-full mr-2" />
                  Follower Growth
                </div>
                <div className="flex items-center text-xs text-gray-600">
                  <div className="w-1.5 h-1.5 bg-pink-500 rounded-full mr-2" />
                  Demographics
                </div>
                <div className="flex items-center text-xs text-gray-600">
                  <div className="w-1.5 h-1.5 bg-pink-500 rounded-full mr-2" />
                  Hashtag Analysis
                </div>
              </div>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-purple-200">
            <div className="flex justify-between text-xs">
              <span className="text-purple-700 font-medium">Data Refresh:</span>
              <span className="text-purple-600">
                Hourly (Standard) • Real-time (Premium)
              </span>
            </div>
            <div className="flex justify-between text-xs mt-1">
              <span className="text-purple-700 font-medium">
                Historical Data:
              </span>
              <span className="text-purple-600">Up to 2 years</span>
            </div>
          </div>
        </div>
      )}

      {/* Security Features */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-900 mb-2">
          Security Features
        </h4>
        <div className="grid grid-cols-2 gap-2">
          {securityFeatures.map((feature) => (
            <div
              key={feature}
              className="flex items-center text-xs text-gray-600"
            >
              <CheckCircleIcon className="w-3 h-3 text-green-500 mr-2 flex-shrink-0" />
              {feature}
            </div>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex space-x-3">
        <button
          onClick={onClose}
          className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleConnect}
          disabled={isLoading}
          className="flex-1 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
        >
          {isLoading ? <LoadingSpinner size="sm" /> : "Connect Account"}
        </button>
      </div>
    </div>
  );

  const renderConnectingStep = () => (
    <div className="text-center space-y-6">
      <div className="animate-pulse">
        <div
          className={`inline-flex items-center justify-center w-16 h-16 rounded-full ${platform.color} ${platform.textColor} mb-4`}
        >
          <span className="text-2xl">{platform.icon}</span>
        </div>
      </div>

      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Connecting to {platform.name}
        </h2>
        <p className="text-gray-600">
          Please complete the authorization in the popup window
        </p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <ExclamationTriangleIcon className="h-5 w-5 text-blue-400 mr-3 mt-0.5 flex-shrink-0" />
          <div className="text-left">
            <h3 className="text-sm font-medium text-blue-800">
              Popup Blocked?
            </h3>
            <p className="text-sm text-blue-700 mt-1">
              If the popup was blocked, please allow popups for this site and
              try again.
            </p>
            {authUrl && (
              <a
                href={authUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800 mt-2"
              >
                <ArrowTopRightOnSquareIcon className="w-4 h-4 mr-1" />
                Open authorization page
              </a>
            )}
          </div>
        </div>
      </div>

      <LoadingSpinner size="lg" />

      <button
        onClick={onClose}
        className="text-gray-500 hover:text-gray-700 text-sm"
      >
        Cancel
      </button>
    </div>
  );

  const renderSuccessStep = () => (
    <div className="text-center space-y-6">
      <div className="animate-bounce">
        <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto" />
      </div>

      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Successfully Connected!
        </h2>
        <p className="text-gray-600">
          Your {platform.name} account has been connected. We're now collecting
          your data.
        </p>
      </div>

      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <h3 className="text-sm font-medium text-green-800 mb-2">
          What happens next?
        </h3>
        <ul className="text-sm text-green-700 space-y-1 text-left">
          <li className="flex items-center">
            <div className="w-1 h-1 bg-green-600 rounded-full mr-2" />
            Initial data sync will begin immediately
          </li>
          <li className="flex items-center">
            <div className="w-1 h-1 bg-green-600 rounded-full mr-2" />
            Historical data will be imported (up to 30 days)
          </li>
          <li className="flex items-center">
            <div className="w-1 h-1 bg-green-600 rounded-full mr-2" />
            Metrics will update every 6 hours
          </li>
          <li className="flex items-center">
            <div className="w-1 h-1 bg-green-600 rounded-full mr-2" />
            You can view insights in your dashboard
          </li>
        </ul>
      </div>
    </div>
  );

  const renderErrorStep = () => (
    <div className="text-center space-y-6">
      <XMarkIcon className="h-16 w-16 text-red-500 mx-auto" />

      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Connection Failed
        </h2>
        <p className="text-gray-600 mb-4">
          We couldn't connect your {platform.name} account.
        </p>
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-left">
        <h3 className="text-sm font-medium text-gray-900 mb-2">
          Common solutions:
        </h3>
        <ul className="text-sm text-gray-700 space-y-1">
          <li className="flex items-center">
            <div className="w-1 h-1 bg-gray-600 rounded-full mr-2" />
            Ensure popups are enabled for this site
          </li>
          <li className="flex items-center">
            <div className="w-1 h-1 bg-gray-600 rounded-full mr-2" />
            Check if you're logged into {platform.name}
          </li>
          <li className="flex items-center">
            <div className="w-1 h-1 bg-gray-600 rounded-full mr-2" />
            Try using an incognito/private window
          </li>
          <li className="flex items-center">
            <div className="w-1 h-1 bg-gray-600 rounded-full mr-2" />
            Contact support if the issue persists
          </li>
        </ul>
      </div>

      <div className="flex space-x-3">
        <button
          onClick={onClose}
          className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
        >
          Close
        </button>
        <button
          onClick={() => {
            setStep("permissions");
            setError(null);
          }}
          className="flex-1 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    </div>
  );

  if (!isOpen || !platform) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h1 className="text-lg font-semibold text-gray-900">
            Connect Social Account
          </h1>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {step === "permissions" && renderPermissionsStep()}
          {step === "connecting" && renderConnectingStep()}
          {step === "success" && renderSuccessStep()}
          {step === "error" && renderErrorStep()}
        </div>
      </div>
    </div>
  );
};

export default ConnectAccountModal;
